import { isTokenValid, decode } from "./utils/JWT";
import AuthTokenStorage from "./AuthTokenStorage";
import AuthStateStorage from "./AuthStateStorage";
import { signOut } from 'aws-amplify/auth';
import { fetchAuthSession } from 'aws-amplify/auth';

// const TOKEN_REVOKE_URL = `${process.env.REACT_APP_API_BASE_URL}/v1/public/auth/revoke`;
const TOKEN_INTROSPECTION_URL = `${process.env.REACT_APP_API_BASE_URL}/v1/public/auth/introspect`;
const GET_TOKENS_URL = `${process.env
  .REACT_APP_API_BASE_URL!}/v1/public/auth/token`;
const IDENTITY_URL = `${process.env
  .REACT_APP_API_BASE_URL!}/v1/private/identity/me`;

type TokenResponse = {
  authorization_token: string;
  refresh_token?: string;
};

const handleTokensResponse = (response: TokenResponse): void => {
  AuthTokenStorage.setAuthorizationToken(response.authorization_token);
  if (response.refresh_token) {
    AuthTokenStorage.setRefreshToken(response.refresh_token);
  }
};

// const clearState = () => {
//   AuthStateStorage.clear();
// };

// const clearTokens = () => {
//   AuthTokenStorage.clear();
// };

// const revokeToken = async (
//   token: string | null,
//   tokenType: string
// ): Promise<void> => {
//   if (!token) {
//     return;
//   }

//   try {
//     await fetch(TOKEN_REVOKE_URL, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         Accept: "application/json",
//       },
//       body: JSON.stringify({
//         token_type: tokenType,
//         [tokenType]: token,
//       }),
//     });
//   } catch (err: any) {
//     console.log(
//       `An error occurred while trying to revoke a ${tokenType}: ${err.message}`
//     );
//   }
// };

const isTokenActive = async (
  token: string | null,
  tokenType: string
): Promise<boolean> => {
  if (!token) {
    return false;
  }

  try {
    const response = await fetch(TOKEN_INTROSPECTION_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        token_type: tokenType,
        [tokenType]: token,
      }),
    });

    const jsonResponse = await response.json();

    if (
      jsonResponse.success === true &&
      jsonResponse.data &&
      jsonResponse.data.is_valid === true
    ) {
      return true;
    }
  } catch (err) {
    // Handle errors here
  }

  return false;
};

const refreshAuthorizationToken = async (): Promise<boolean> => {
  const refreshToken = AuthTokenStorage.getRefreshToken();

  if (!refreshToken || !isTokenActive(refreshToken, "refresh_token")) {
    return false;
  }

  try {
    const response = await fetch(GET_TOKENS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
      }),
    });

    const jsonResponse = await response.json();

    if (
      jsonResponse.success &&
      jsonResponse.success === true &&
      jsonResponse.data
    ) {
      handleTokensResponse(jsonResponse.data);

      return true;
    }
  } catch (err: any) {
    console.log(
      `An error occurred while trying to refresh tokens: ${err.message}`
    );
  }

  return false;
};

const login = async ({
  authorizationCode = null,
  redirect = true,
  restoreState = true,
}: {
  authorizationCode?: string | null;
  redirect?: boolean;
  restoreState?: boolean;
} = {}): Promise<boolean> => {
  const urlParams = new URLSearchParams(window.location.search);
  if (!authorizationCode) {
    authorizationCode = urlParams.get("code");
  }

  if (!authorizationCode) {
    return false;
  }

  try {
    const response = await fetch(GET_TOKENS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        grant_type: "authorization_code",
        authorization_code: authorizationCode,
        redirect_uri: `${process.env.REACT_APP_USER_POOL_LOGIN_REDIRECT_URL}`,
      }),
    });

    const jsonResponse = await response.json();

    if (
      jsonResponse.success &&
      jsonResponse.success === true &&
      jsonResponse.data
    ) {
      handleTokensResponse(jsonResponse.data);

      if (redirect) {
        const state = AuthStateStorage.getAndDestroy();

        if (state && restoreState) {
          window.location.href = state.location.href;
        } else {
          window.location.href = `${process.env.REACT_APP_FRONTEND_BASE_URL}`;
        }
      }

      return true;
    }
  } catch (err: any) {
    console.log(
      `An error occurred while obtaining new tokens from auth code: ${err.message}`
    );
  }

  return false;
};

async function logout() {
  try {
    await signOut({ global: true });
    AuthTokenStorage.clear();

    window.location.href = `/logout`;
  } catch (error) {
    console.log('error signing out: ', error);
  }
}

const getValidAuthorizationToken = async ({
  redirectToLogin = true,
}: {
  redirectToLogin?: boolean;
} = {}): Promise<string | null> => {
  try {
    let authorizationToken = AuthTokenStorage.getAuthorizationToken();

    if (authorizationToken && isTokenValid(authorizationToken)) {
      return authorizationToken;
    } else {
      await refreshAuthorizationToken();
    }

    authorizationToken = AuthTokenStorage.getAuthorizationToken();

    if (authorizationToken && isTokenValid(authorizationToken)) {
      return authorizationToken;
    }
  } catch (err: any) {
    console.log(
      `An error occurred while trying to obtain a valid authorization token: ${err.message}`
    );

    const refreshToken = AuthTokenStorage.getRefreshToken();

    if (refreshToken) {
      const tokenRefreshSucceed = await refreshAuthorizationToken();

      if (tokenRefreshSucceed) {
        const authorizationToken = AuthTokenStorage.getAuthorizationToken();

        if (authorizationToken && isTokenValid(authorizationToken)) {
          return authorizationToken;
        }
      }
    }
  }

  if (redirectToLogin) {
    await logout();

    window.location.href = `/login`;
  }

  return null;
};

const getIdentity = async ({ redirectToLogin = true, }: { redirectToLogin?: boolean; } = {}): Promise<any | null> => {
  try {
    const { idToken } = (await fetchAuthSession()).tokens ?? {};
    AuthTokenStorage.setAuthorizationToken(`${idToken}`);
    const autorizationToken = idToken;

    if (!autorizationToken) {
      throw new Error("Unauthorized");
    }

    const response = await fetch(IDENTITY_URL, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${autorizationToken}`,
      },
    });

    if (response.status === 401) {
      throw new Error("Unauthorized");
    }

    const jsonResponse = await response.json();
    if (jsonResponse.success && jsonResponse.success === true && jsonResponse.data) {
      console.log(jsonResponse.data);
      return jsonResponse.data;
    }
  } catch (err: any) {
    console.log(`An error occurred while obtaining user identity: ${err.message}`);
    if (err.message === "Unauthorized" && redirectToLogin) {
      // Navigate to the login page
      window.location.href = "/login";
    }
  }
  return null;
};

const getMinimumSessionLifespan = async (): Promise<number> => {
  const refreshToken = AuthTokenStorage.getRefreshToken();

  if (await isTokenActive(refreshToken, "refresh_token")) {
    return 3600;
  }

  const authorizationToken = AuthTokenStorage.getAuthorizationToken();

  if (await isTokenActive(authorizationToken, "authorization_token")) {
    const decodedAuthorizationToken = decode(authorizationToken);

    if (decodedAuthorizationToken && decodedAuthorizationToken.lifespan) {
      return decodedAuthorizationToken.lifespan;
    }
  }

  return 0;
};

const AuthProvider = {
  logout,
  login,
  getValidAuthorizationToken,
  getIdentity,
  getMinimumSessionLifespan,
};

export default AuthProvider;
